import { attachmentContentUrl, attachmentDownloadUrl } from '../../api/endpoints'
import type { Attachment } from '../../api/types'
import { isImageType, MAX_FILE_BYTES } from '../../lib/files'
import { isClip } from '../../lib/media'
import { pluralize } from '../../lib/format'
import { toast } from '../../state/toasts'

/**
 * Whether this device can fetch a file: someone else's video or audio only once it's converted, which removes where it
 * was recorded (contract: "Attachments", fetching before it is converted). Its sender always can.
 */
export function canFetchAttachment(attachment: Attachment, mine: boolean): boolean {
  return mine || (attachment.media !== 'processing' && attachment.media !== 'failed')
}

/** What Save and Share act on: "photo", "video", "voice message", "file", "3 photos", "2 files". */
export function filesNoun(attachments: Attachment[]): string {
  const all = (test: (a: Attachment) => boolean) => attachments.every(test)
  const noun = all((a) => isImageType(a.contentType))
    ? 'photo'
    : all((a) => a.kind === 'video')
      ? 'video'
      : all((a) => a.kind === 'audio' && a.voice)
        ? 'voice message'
        : 'file'
  return attachments.length === 1 ? noun : pluralize(attachments.length, noun)
}

async function download(attachment: Attachment): Promise<File> {
  // Redirects to the storage link, which allows the app's origin (see the deployment notes on CORS).
  const response = await fetch(attachmentContentUrl(attachment.id, 'original'), { credentials: 'same-origin' })
  if (!response.ok) throw new Error(`Download failed (${response.status})`)
  return new File([await response.blob()], attachment.fileName, { type: attachment.contentType })
}

/** Browsers refuse downloads that start together, so each waits a moment for the one before. */
const DOWNLOAD_GAP_MS = 400

function clickLink(href: string, fileName: string): void {
  const link = document.createElement('a')
  link.href = href
  link.download = fileName
  document.body.append(link)
  link.click()
  link.remove()
}

/**
 * Saves each file under its own name; the storage link itself would open photos and PDFs in the browser. A video or audio
 * clip, which can be large, downloads straight from storage as a download instead of being read into memory first.
 */
export async function saveAttachments(attachments: Attachment[]): Promise<void> {
  let saved = 0
  for (const attachment of attachments) {
    if (isClip(attachment)) {
      if (saved > 0) await new Promise((resolve) => setTimeout(resolve, DOWNLOAD_GAP_MS))
      clickLink(attachmentDownloadUrl(attachment.id), attachment.fileName)
      saved++
      continue
    }
    try {
      const url = URL.createObjectURL(await download(attachment))
      clickLink(url, attachment.fileName)
      saved++
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch {
      toast(`Couldn't save ${attachment.fileName}`, { tone: 'error' })
    }
  }
}

/**
 * Whether the device's share sheet takes these files: phones do, most desktop browsers don't. Sharing reads each file into
 * memory first, so a video over 25 MB isn't offered.
 */
export function canShareFiles(attachments: Attachment[]): boolean {
  if (attachments.some((a) => isClip(a) && a.sizeBytes > MAX_FILE_BYTES)) return false
  try {
    return (
      typeof navigator.share === 'function' &&
      typeof navigator.canShare === 'function' &&
      navigator.canShare({ files: attachments.map((a) => new File([], a.fileName, { type: a.contentType })) })
    )
  } catch {
    return false
  }
}

export async function shareAttachments(attachments: Attachment[]): Promise<void> {
  const noun = filesNoun(attachments)
  let files: File[]
  try {
    files = await Promise.all(attachments.map(download))
  } catch {
    toast(`Couldn't share the ${noun}`, { tone: 'error' })
    return
  }
  const share = (): Promise<void> =>
    navigator.share({ files }).catch((error: unknown) => {
      const name = error instanceof DOMException ? error.name : ''
      if (name === 'AbortError') return
      if (name === 'NotAllowedError') {
        // The download took longer than the browser lets a tap count for: share on the next tap.
        toast(`The ${noun} ${attachments.length === 1 ? 'is' : 'are'} ready to share`, {
          action: { label: 'Share', onClick: () => void share() },
          duration: 10_000,
        })
        return
      }
      toast(`Couldn't share the ${noun}`, { tone: 'error' })
    })
  await share()
}
